/**
 * Parse order_num into a tuple of integers for dotted-numeric comparison.
 * Splits on '.' and compares segment-wise so that 1.2 comes before 1.11 (since [1,2] < [1,11]).
 *
 * Example:
 *   order_num=1.11 -> [1, 11]
 *   order_num=1.2  -> [1, 2]
 *   order_num=1    -> [1]
 *   order_num=null -> [0]  // Nulls sort first
 */

export const parseOrderNum = (value: number | null | undefined): number[] => {
    if (value == null) return [0];
    const strVal = String(value);
    const parts = strVal.split('.');
    return parts.map(p => {
        const n = parseInt(p.replace(/[^0-9]+/g, ''), 10);
        return Number.isNaN(n) ? 0 : n;
    });
};

/**
 * Compare two order_num values using dotted-numeric comparison.
 * Returns negative if a < b, positive if a > b, 0 if equal.
 */

export const compareOrderNum = (a: number | null | undefined, b: number | null | undefined): number => {
    const segsA = parseOrderNum(a);
    const segsB = parseOrderNum(b);
    const maxLen = Math.max(segsA.length, segsB.length);
    
    for (let i = 0; i < maxLen; i++) {
        const valA = i < segsA.length ? segsA[i] : 0;
        const valB = i < segsB.length ? segsB[i] : 0;
        if (valA !== valB) return valA - valB;
    }
    return 0;
};

/**
 * Sort an array of items by their order_num field using dotted-numeric comparison.
 * Mutates and returns the array.
 */
export function sortByOrderNum<T extends { order_num?: number | null }>(items: T[]): T[] {
    return items.sort((a, b) => compareOrderNum(a.order_num, b.order_num));
}